import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { DefaultDataService } from 'src/app/services/default-data.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { EventService } from 'src/app/services/event.service';
import { SessionContextService } from 'src/app/services/session-context.service';
import { Employee } from '../../model/employee.model';
import { Country } from '../../model/country';
import { AsYouType, isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js/mobile';
import { ConstantsService } from 'src/app/services/constants.service';
import { Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';

@Component({
  selector: 'app-employee-profile',
  templateUrl: './employee-profile.component.html',
  styleUrls: ['./employee-profile.component.css']
})
export class EmployeeProfileComponent implements OnDestroy {
  @Input() public popupVisible: boolean = false;
  @ViewChild(DxFormComponent, { static: false }) form: DxFormComponent
  public employee: Employee | null = null;
  country: Country;
  countries: Country[];
  defaultCountry: Country;
  employeeMobileDisplayValue: string = '';
  SaveButtonOptions = {
    text: 'Save',
    onClick: this.btnSave.bind(this)
  };
  CancelButtonOptions = {
    text: 'Cancel',
    onClick: this.btnCancel.bind(this),
  };
  defaultDataChanged$: Subscription;

  constructor(private events: EventService,
    private session: SessionContextService,
    private employeeAuth: EmployeeService,
    public constants: ConstantsService,
    private defData: DefaultDataService) {
    this.employee = Employee.CreateFrom(this.session.ctx.Employee);        
    this.CheckPINLength1 = this.CheckPINLength1.bind(this);
    this.CheckPINLength2 = this.CheckPINLength2.bind(this);
    this.CheckPasswordLength1 = this.CheckPasswordLength1.bind(this);
    this.CheckPasswordLength2 = this.CheckPasswordLength2.bind(this);
    this.validateMobileFormat = this.validateMobileFormat.bind(this);

    this.defaultDataChanged$ = this.events.onDefaultDataChanged.pipe(first()).subscribe(_ => {
      this.countries = this.constants.Countries;
      this.defaultCountry = this.constants.DefaultCountry;
    })
  }

  ngOnDestroy(): void {
    this.defaultDataChanged$?.unsubscribe();
  }

  formInstance: any = null;
  onInitialized(e) {
    this.formInstance = e.component;
  }

  // Work item 6471
  CheckPINLength1(e) {
    if (e.value.trim().length == 0)
      return true;
    return (e.value.trim().length == 4);
  }
  CheckPINLength2(e) {
    if (e.value.trim().length == 0)
      return true;
    return (e.value.trim().length == 4);
  }

  CheckPasswordLength1(e) {
    if (e.value.trim().length == 0)
      return true;
    return (e.value.trim().length >= 6 && e.value.trim().length <= 50);
  }
  CheckPasswordLength2(e) {
    if (e.value.trim().length == 0)
      return true;
    return (e.value.trim().length >= 6 && e.value.trim().length <= 50);
  }

  validateMobileFormat(e: any): boolean {
    return e?.value.length ? isValidPhoneNumber(e.value, this.country?.ISO2) : true;
  }

  pinComparison1 = () => {
    return this.form.instance.option("formData").ChangePIN1;
  };

  passwordComparison = () => {
    return this.form.instance.option("formData").ChangePassword1;
  };

  onShowing(e) {
    this.employee = Employee.CreateFrom(this.session.ctx.Employee);

    if (!!this.employee.MobileTelephone) {
      try {
        const phoneNumber = parsePhoneNumberWithError(this.employee.MobileTelephone);
        if (phoneNumber.country !== this.country?.ISO2) {
          this.country = this.countries.find(country => {
            return country.ISO2 === phoneNumber.country;
          });
        }
        const format = new AsYouType(this.country?.ISO2);
        this.employeeMobileDisplayValue = format
          .input(phoneNumber.number)
          .replace(`+${this.country?.ISD}`, '')
          .replace('+', '')
          .trim();
      } catch {
        this.country = this.defaultCountry
        this.employeeMobileDisplayValue = this.employee.MobileTelephone.trim();
      }
    } else {
      this.country = this.defaultCountry;
      this.employeeMobileDisplayValue = '';
    }
  }

  onMobileISDSelected(e: any): void {
    this.country = e.value;
  }

  onMobileInput(e: any): void {
    const format = new AsYouType(this.country?.ISO2);
    const example = new AsYouType(this.country?.ISO2);
    example.input(this.country.exampleNumber);
    const prefix = '+' + example.getCallingCode();
    const max = this.country.maxLength;

    let newVal: string = e.event.target.value.replace(/\D/g, '').trim();

    if (newVal.replace(/\s/g, '').length > max - 1) {
      newVal = newVal.slice(0, newVal.length - (newVal.length - max));
    }

    newVal = format
      .input(prefix + newVal)
      .replace(prefix, '')
      .trim();

    e.event.target.value = newVal;
    this.employee.MobileTelephone = newVal;
  }

  public btnSave(a: any) {
    try {
      if (this.employee.ChangePIN1 && this.employee.ChangePIN1.length > 0 && this.employee.ChangePIN1 != this.employee.ChangePIN2) {
        notify("There is an error on the form. Please check that New PIN and the Confirm PIN match.", "error", 5000);
        return;
      }
      if (this.employee.ChangePassword1 && this.employee.ChangePassword1.length > 0 && this.employee.ChangePassword1 != this.employee.ChangePassword2) {
        notify("There is an error on the form. Please check that the New Password matches the Confirm Password.", "error", 5000);
        return;
      }
    }
    catch (err) {
    }

    try {
      this.employee.MobileTelephone = parsePhoneNumberWithError(
        this.employee.MobileTelephone,
        this.country?.ISO2
      ).formatInternational();
    } catch (err) {
      this.employee.MobileTelephone = '';
    }

    this.employeeAuth
      .UpdateEmployee(this.employee)
      .pipe(take(1))
      .subscribe((r) => {
        if (r.Succeeded == true) {
          this.popupVisible = false;
          this.employee.ChangePIN1 = '';
          this.employee.ChangePIN2 = '';
          this.employee.ChangePassword1 = '';
          this.employee.ChangePassword2 = '';
          notify(r.Msg, r.Success ? 'success' : 'warning', 5000);

          // Update local data
          var e = this.defData.Employees.find((i) => i.UID == this.employee.UID);
          if (e) {
            e.Title = this.employee.Title;
            e.FirstName = this.employee.FirstName;
            e.LastName = this.employee.LastName;
            e.MobileTelephone = this.employee.MobileTelephone;
          }
          e = this.session.ctx.Employee;
          if (e) {
            e.Title = this.employee.Title;
            e.FirstName = this.employee.FirstName;
            e.LastName = this.employee.LastName;
            e.MobileTelephone = this.employee.MobileTelephone;
          }
          this.events.UserUpdated();
        } else {
          notify(r.Msg, r.notifyType, 5000);
          this.btnCancel(null);
        }
      });
  }

  public btnCancel(a: any) {
    this.popupVisible = false;
    this.employee = Employee.CreateFrom(this.session.ctx.Employee);
  }
}
